<template>
  <div v-if="subfolders.length > 0" class="subfolder_section">
    <p v-if="subfolderOrdering" class="ordering_instructions">
      {{ $t("workspace.ordering.instructions") }}
    </p>
    <WorkspaceOrderList
      class="subfolder_grid"
      layout="grid"
      :items="subfolders"
      :editing="subfolderOrdering"
      :disabled="subfolderOrderingDisabled"
      :handle-label="t('workspace.ordering.moveHandle')"
      :instructions="t('workspace.ordering.instructions')"
      @move="emit('moveSubfolder', $event)"
      @gesture="emit('subfolderOrderingGesture', $event)"
    >
      <template #item="{ item: subfolder, canDrag, handleProps }">
        <div class="subfolder_order_tile">
          <button
            v-if="subfolderOrdering && canDrag"
            type="button"
            class="subfolder_drag_handle"
            v-bind="handleProps"
          >
            <span class="material-symbols-outlined">drag_indicator</span>
          </button>
          <SubfolderCard :workspace="subfolder" />
        </div>
      </template>
    </WorkspaceOrderList>
  </div>

  <template v-if="documents && documents.length > 0">
    <template
      v-if="store.getters.currentWorkspace?.root?.myPrivileges?.mayAdministrate"
    >
      <draggable-grid
        class="columns grid_drag"
        @placeAbove="placeAbove"
        @placeBelow="placeBelow"
      >
        <template v-for="(doc, idx) in documents" :key="doc!.id">
          <DocCard
            v-if="doc"
            @click="navigateToProjector(doc!.firestoreId)"
            @move="emit('move', doc.id)"
            @share="emit('share', doc!.id)"
            class="column"
            :doc="doc"
            :data-idx="idx"
            :data-id="doc!.id"
            :data-test="`document-${doc.displayTitle}`"
          >
          </DocCard>
        </template>
      </draggable-grid>
    </template>
    <template v-else>
      <div class="columns">
        <template v-for="(doc, idx) in documents" :key="doc!.id">
          <DocCard
            v-if="doc"
            @click="navigateToProjector(doc!.firestoreId)"
            @move="emit('move', doc.id)"
            @share="emit('share', doc!.id)"
            class="column"
            :key="doc!.id"
            :doc="doc"
          >
          </DocCard>
        </template>
      </div>
    </template>
  </template>
</template>

<script lang="ts" setup>
import { reactive, PropType } from "vue";
import { DraggableGrid, SubfolderCard } from "@/components";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import {
  useReorderDocumentMutation,
  WorkspaceDocumentFragment,
  ChildWorkspaceFragment,
} from "@/graphql/operations";
import DocCard from "./DocCard.vue";
import WorkspaceOrderList from "@/components/workspace-ordering/WorkspaceOrderList.vue";
import type { WorkspaceMove } from "@/composables/useWorkspaceOrdering";
import { useI18n } from "vue-i18n";

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const emit = defineEmits<{
  share: [id: string];
  move: [id: string];
  moveSubfolder: [move: WorkspaceMove];
  subfolderOrderingGesture: [active: boolean];
}>();

const props = defineProps({
  subfolders: {
    type: Object as PropType<Array<ChildWorkspaceFragment>>,
    required: true,
  },
  documents: {
    type: Object as PropType<Array<WorkspaceDocumentFragment>>,
    required: true,
  },
  subfolderOrdering: {
    type: Boolean,
    default: false,
  },
  subfolderOrderingDisabled: {
    type: Boolean,
    default: false,
  },
});

function navigateToProjector(pvDocId: string) {
  /* navigate to projector */
  router.push({
    name: "StackedProjector",
    params: {
      workspaceId: route.params.workspaceId,
    },
    query: {
      fid: pvDocId,
    },
  });
}

const { executeMutation: reorderDocument } = useReorderDocumentMutation();
async function placeAbove(docId: string, aboveId: string) {
  await reorderDocument({
    documentId: docId,
    aboveId: aboveId,
  });
}

async function placeBelow(docId: string, belowId: string) {
  await reorderDocument({
    documentId: docId,
    belowId: belowId,
  });
}
</script>

<style lang="scss" scoped>
:deep(.subfolder_grid) {
  grid-gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  margin-bottom: 2em;
}

.ordering_instructions {
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
  font-size: 0.8rem;
  margin: 0 0 0.45rem;
}

.subfolder_order_tile {
  min-width: 0;
  position: relative;
}

.subfolder_drag_handle {
  align-items: center;
  background: var(--classic-surface, #fff);
  border: 1px solid var(--classic-border, #dbdbdb);
  border-radius: 50%;
  color: var(--classic-text-secondary, #666);
  cursor: grab;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  left: 0.35rem;
  padding: 0;
  position: absolute;
  top: 0.35rem;
  width: 32px;
  z-index: 3;
}

.subfolder_drag_handle:active {
  cursor: grabbing;
}

@media (max-width: 767px), (any-pointer: coarse) {
  .subfolder_drag_handle {
    height: 44px;
    width: 44px;
  }
}

.info {
  height: calc(100vh - 40px - 2em - 38px - 0.8rem);
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.info .text {
  opacity: 0.8;
}

.info .material-symbols-outlined {
  font-size: 14rem;
  color: var(--classic-border-subtle, #ebebeb);
  margin-top: -2rem;
}

.loading_wrapper {
  height: 100%;

  .loading_status {
    height: 75%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
